import { useCallback, useMemo } from "react"

import * as SettingsAPI from "@/api/useSettings"
import { useCreateSastGuidance, useUpdateSastGuidance } from "@/api/useSettings"
import { useGetTenantConfigurations } from "@/api/useTenant"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { predicateHelpers } from "@/hooks/useHttp"
import * as selectors from "./useGuidance.selector"

import type { SastGuidanceResponse } from "@/api/useSettings.types"
import type { QueryClient } from "@/hooks/useHttp"

export const useWithTenantConfigurations = <T extends { isLoading: boolean }>(
  ctx: T
) => {
  const { response, isLoading } = useGetTenantConfigurations()
  const { data } = response || {}
  const kaiSastLlm = data?.kaiSastLlm || false
  const { isLoading: isLoadingCtx, ...rest } = ctx

  return [
    { isLoading: isLoadingCtx || isLoading, ...rest },
    { sastLlmDisabled: kaiSastLlm !== true }
  ] as const
}

export const useGetOptionsSastGuidance = () => {
  const optionsCtx = SettingsAPI.useGetOptionsSastGuidance()
  const scopeOptionsCtx = SettingsAPI.useGetScopeOptionsSastGuidance()

  const isLoading = optionsCtx.isLoading || scopeOptionsCtx.isLoading
  const options = useMemo(() => {
    if (isLoading) return undefined

    const optionsData = optionsCtx.response?.data
    const scopeOptionsData = scopeOptionsCtx.response?.data

    if (!optionsData || !scopeOptionsData) return undefined

    const { repoNames, ...restOptions } = optionsData
    const rest = Object.keys(restOptions).reduce(
      (acc, key) => {
        const values = restOptions[key]

        if (values)
          acc[key as "cwes" | "ruleIds"] = values.map((item) => ({
            label: item,
            value: item
          }))

        if (key === selectors.FIELD_NAMES.cwes)
          acc[key as "cwes"] = acc[key as "cwes"].sort((a, b) => {
            const aNumber = Number(a.label.replace(/CWE-/, ""))
            const bNumber = Number(b.label.replace(/CWE-/, ""))

            return aNumber - bNumber
          })

        return acc
      },
      {} as Record<
        keyof typeof selectors.FIELD_NAMES,
        { label: string; value: string }[]
      >
    )

    return {
      scopes: (scopeOptionsData || []).map(({ id, displayName, ...rest }) => {
        if (id === selectors.FIELD_NAMES.repositoryName)
          return {
            ...rest,
            value: id,
            label: displayName,
            opts: (repoNames || []).map((item) => ({
              label: item,
              value: item,
              displayName: item,
              id: item
            }))
          }

        return { id, displayName, label: displayName, value: id, ...rest }
      }),
      ...rest
    }
  }, [isLoading, optionsCtx.response, scopeOptionsCtx.response])

  return {
    isLoading,
    data: options
  }
}

export const useGetSastGuidance = () => {
  SettingsAPI.useGetScopeOptionsSastGuidance() //prefetch scope options for guidance form

  const scopeOptionsContext = SettingsAPI.useGetScopeOptionsSastGuidance({
    options: "false"
  })
  const scopes = scopeOptionsContext.response?.data
  const select = useCallback(
    (response: SastGuidanceResponse) =>
      selectors.useGetSastGuidanceSelector(response, scopes),
    [scopes]
  )

  const context = SettingsAPI.useGetSastGuidance({
    select
  })
  const [{ isLoading, ...restCtx }, ...rest] =
    useWithTenantConfigurations<typeof context>(context)

  return [
    { isLoading: isLoading || scopeOptionsContext.isLoading, ...restCtx },
    ...rest
  ] as const
}

export const useActionsSastGuidance = () => {
  const config = (queryClient: QueryClient) => ({
    onSuccess: (_: any, variable: { needle: string }) => {
      const { GUIDANCE } = RESOURCES_NAMES.SETTINGS.SAST

      return queryClient.refetchQueries({
        predicate: predicateHelpers.includesKeysPredicate(
          [GUIDANCE.ROOT, `${GUIDANCE.ROOT}${variable?.needle}`],
          [GUIDANCE.OPTIONS, GUIDANCE.SCOPE_OPTIONS]
        )
      })
    }
  })

  const createCtx = useCreateSastGuidance(config)
  const updateCtx = useUpdateSastGuidance(undefined, config)

  return {
    createCtx,
    updateCtx
  }
}
